import { Stack } from "@mui/material";
import React, { Dispatch, DispatchWithoutAction, SetStateAction, useEffect } from "react";
import { useOutletContext } from "react-router-dom";
import { IControl, TriggeredHook } from "sonobello.utilities.react";

import { ContactResult } from "../../../../dtos/ContactResult";
import { Step } from "../../../../types/Step";
import { isMobileView } from "../../../../utils/Constants";
import { INonCandidateViewRouter } from "../../../Routing/Types/IRouter";
import { IFlowStepProps } from "../../../Types/IFlow";
import StepWrapper, { IStepWrapperButtonProps } from "../../Components/StepWrapper";
import IMedicalConclusions from "../Types/IMedicalConclusions";
import IMedicalFormElement from "../Types/IMedicalFormElement";
import IMedicalQualification from "../Types/IMedicalQualification";
import IMedicalQuestionnaireCompleteEvaluator from "../Types/IMedicalQualificationCompleteEvaluator";
import IMedicalQualificationNonCandidateEvaluator from "../Types/IMedicalQualificationNonCandidateEvaluator";

/** The run-time properties which control the medical step. */
export interface IMedicalStepProps extends IControl<IMedicalQualification> {
  /** The router to use to navigate to the non-candidate view. */
  router: INonCandidateViewRouter;
  /** A callback invoked when the user is disqualified as a non-candidate. */
  onMedicalNoncandidateDisqualification: DispatchWithoutAction;
  /** State setter for the medical form. */
  setMedicalFormState: Dispatch<SetStateAction<IMedicalQualification>>;
  /** State setter for the medical form conclusions. */
  setMedicalConclusions: Dispatch<SetStateAction<IMedicalConclusions>>;
}

/** The composed properties of the medical step. */
export interface IMedicalStepConfig {
  /** Evaluates the qualifications set for the patient, determining if they are complete and disqualify the user. */
  medicalQualificationEvaluator: IMedicalQualificationNonCandidateEvaluator & IMedicalQuestionnaireCompleteEvaluator;
  /** The path to navigate to when the user clicks the next button and is not disqualified. */
  nextStep: Step;
  /** The forms which will be rendered to the user. */
  FormQuestions: React.FC<IMedicalFormElement>[];
  /** A hook to be called when the user is disqualified as a non-candidate for medical reasons. */
  useCreateNonCandidateContactResult: TriggeredHook<ContactResult, undefined, boolean>;
}

/** A stand-alone medical form step component, which renders questions to be answered to the user.
 * The configured evaluators decide when all required answers are complete, and if those answers disqualify the user.
 * Calls the configured hook to create a disqualifying contact result if the user is disqualified.
 */
const FormSeriesMedicalStep: React.FC<IMedicalStepProps & IMedicalStepConfig & IStepWrapperButtonProps> = ({
  medicalQualificationEvaluator,
  nextStep,
  router,
  value,
  onMedicalNoncandidateDisqualification,
  setMedicalConclusions,
  setMedicalFormState,
  useCreateNonCandidateContactResult,
  FormQuestions,
  NextButtonMobile,
  NextButtonDesktop
}) => {
  const isMobile = isMobileView();
  const flowStepProps = useOutletContext<IFlowStepProps>();
  const { execute: createNonCandidateContactResult, result, isLoading } = useCreateNonCandidateContactResult();

  const handleNext = () => {
    const isNonCandidate = medicalQualificationEvaluator.isNonCandidate(value);
    if (isNonCandidate) {
      onMedicalNoncandidateDisqualification();
      createNonCandidateContactResult();
    } else flowStepProps.setStep(nextStep);
  };

  useEffect(() => {
    if (result) router.goToNonCandidateView();
  }, [result]);

  return (
    <StepWrapper
      NextButtonMobile={NextButtonMobile}
      NextButtonDesktop={NextButtonDesktop}
      nextButtonLoading={isLoading}
      onNext={handleNext}
      disabled={isLoading || !medicalQualificationEvaluator.isComplete(value)}
    >
      <Stack spacing={1.5}>
        {FormQuestions.map((Question, index) => (
          <Question
            key={index}
            value={value}
            onChange={s => setMedicalFormState(o => ({ ...o, ...s }))}
            isMobile={isMobile}
            setMedicalConclusions={setMedicalConclusions}
            setMedicalFormState={setMedicalFormState}
          />
        ))}
      </Stack>
    </StepWrapper>
  );
};

export default FormSeriesMedicalStep;
